import { CoreButton, CoreIconButton, CoreMenuItem, CoreSelect, OverflowText } from "@verity/components/core";
import i18n from "../../../../../i18n";
import { FlagLevelBadge } from "../../../../../reports/shared/report-components";
import styles from "./StudentSelector.module.css";
import ArrowIcon from "../../../../../images/reports/backIcon.svg?react";
import { Box } from "@mui/material";

type StudentButtonProps = {
	label: string;
	type: "text" | "icon";
	onClick: () => void;
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
};

function StudentButton({ label, type, onClick, startIcon, endIcon }: StudentButtonProps) {
	if (type === "icon") {
		return (
			<CoreIconButton onClick={onClick} aria-label={label} variant="plain" className={styles.studentIconButton}>
				{startIcon ?? endIcon}
			</CoreIconButton>
		);
	}

	return (
		<CoreButton
			className={styles.studentButton}
			onClick={onClick}
			startIcon={startIcon}
			endIcon={endIcon}
			applyColorToSvg
		>
			{label}
		</CoreButton>
	);
}

function StudentOptionDisplay({ name, flagLevel }: { name: string; flagLevel: number }) {
	return (
		<div className={styles.studentDisplay}>
			<OverflowText>{name}</OverflowText>
			<FlagLevelBadge flagLevel={flagLevel} />
		</div>
	);
}

type Student = {
	id: number;
	name: string;
	severityLevel?: number;
};

type StudentSelectorProps = {
	selectedStudent?: number;
	onStudentChange: (studentId?: number) => void;
	studentList: Student[];
	className?: string;
	removeBorderStyling?: boolean;
};

function StudentSelector({
	selectedStudent,
	onStudentChange,
	studentList,
	className,
	removeBorderStyling = true,
}: StudentSelectorProps) {
	return (
		<CoreSelect
			className={`${styles.studentSelector} ${className || ""}`}
			sx={{
				fontWeight: 600,
				fontSize: "20px",
				height: "40px",
				maxWidth: { md: "calc(100% - 200px)", sm: "100%", xs: "100%" },
				"& .MuiSelect-icon": {
					top: "11px",
				},

				...(removeBorderStyling && {
					border: "none",
					"& .MuiOutlinedInput-notchedOutline": {
						border: "none",
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						border: "none",
					},
				}),
			}}
			label={i18n.t("result.components.resultsPage.ariaLabels.studentComboBox")}
			value={selectedStudent}
			onChange={(event) => {
				const studentId = Number(event.target.value);
				onStudentChange(studentId);
			}}
			renderValue={(selectedValue) => {
				const student = studentList.find((s) => s.id === selectedValue);
				if (!student) return null;
				return <StudentOptionDisplay name={student.name} flagLevel={student.severityLevel ?? 0} />;
			}}
		>
			{studentList.map((student, index) => (
				<CoreMenuItem key={index} value={student.id}>
					<StudentOptionDisplay name={student.name} flagLevel={student.severityLevel ?? 0} />
				</CoreMenuItem>
			))}
		</CoreSelect>
	);
}

type StudentSelectorLayoutProps = Omit<StudentSelectorProps, "className"> & {
	buttonType: StudentButtonProps["type"];
	onBack: () => void;
	onNext: () => void;
	studentSelectorClassName?: string;
	className?: string;
};

export default function StudentSelectorLayout({
	selectedStudent,
	onStudentChange,
	studentList,
	buttonType,
	onBack,
	onNext,
	studentSelectorClassName,
	removeBorderStyling,
	className,
}: StudentSelectorLayoutProps) {
	return (
		<Box
			className={`${styles.studentSelectorLayout} ${className || ""}`}
			flexDirection={{ xs: "column", md: "row" }}
			justifyContent={studentList.length > 1 ? "space-between" : "center"}
		>
			{studentList.length > 1 && (
				<StudentButton
					type={buttonType}
					label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.previous")}
					onClick={onBack}
					startIcon={<ArrowIcon style={{ transform: "rotate(180deg)", height: "14px" }} />}
				/>
			)}
			<StudentSelector
				selectedStudent={selectedStudent}
				onStudentChange={onStudentChange}
				studentList={studentList}
				className={studentSelectorClassName}
				removeBorderStyling={removeBorderStyling}
			/>
			{studentList.length > 1 && (
				<StudentButton
					type={buttonType}
					label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.next")}
					onClick={onNext}
					endIcon={<ArrowIcon style={{ marginLeft: buttonType === "text" ? "8px" : "0", height: "14px" }} />}
				/>
			)}
		</Box>
	);
}
